import { useDraggable } from '@dnd-kit/core'
import { useRef, useState, type FC } from 'react'
import {
  STAGE_CONFIG,
  scoreColor,
  type ProofPoint,
  type Stage,
} from '../domain/Bet.domain'
import { useDeleteProofPoint } from '../repositories/useDeleteProofPoint'
import { useGetPersons } from '../repositories/useGetPersons'
import { ScoreIndicator } from './ScoreIndicator'
import { ScoreSelector } from './ScoreSelector'

interface Props {
  proof: ProofPoint
  stage: Stage
  betId: string
  onChange: (updated: ProofPoint) => void
}

export const ProofCard: FC<Props> = ({ proof, stage, betId, onChange }) => {
  const cfg = STAGE_CONFIG[stage]
  const { mutate: deleteProofPoint, isPending: isDeleting } =
    useDeleteProofPoint()
  const { data: persons = [] } = useGetPersons()
  const [isEditingText, setIsEditingText] = useState(false)
  const [editText, setEditText] = useState(proof.text)
  const textInputRef = useRef<HTMLInputElement>(null)

  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: proof.id,
      data: { proofId: proof.id, betId, fromStage: stage },
    })

  const dragStyle = transform
    ? { transform: `translate(${transform.x}px, ${transform.y}px)` }
    : undefined

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={{
        fontSize: 13,
        lineHeight: 1.45,
        padding: '8px 10px',
        background: cfg.bg,
        borderRadius: 8,
        border: `1px solid ${cfg.border}`,
        borderLeft: `3px solid ${scoreColor(proof.score)}`,
        opacity: isDragging || isDeleting ? 0.4 : 1,
        cursor: 'grab',
        userSelect: 'none',
        zIndex: isDragging ? 50 : undefined,
        ...dragStyle,
      }}
    >
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <ScoreIndicator score={proof.score} size={14} />
        <div
          style={{ flex: 1, minWidth: 0, color: '#1f2937', cursor: 'text' }}
          onClick={(e) => {
            e.stopPropagation()
            setIsEditingText(true)
            setEditText(proof.text)
            setTimeout(() => textInputRef.current?.focus(), 0)
          }}
        >
          {isEditingText ? (
            <input
              ref={textInputRef}
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              onBlur={() => {
                setIsEditingText(false)
                const trimmed = editText.trim()
                if (trimmed && trimmed !== proof.text) {
                  onChange({ ...proof, text: trimmed })
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  ;(e.target as HTMLInputElement).blur()
                }
                if (e.key === 'Escape') {
                  setEditText(proof.text)
                  setIsEditingText(false)
                }
              }}
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              style={{
                fontSize: 13,
                border: '1px solid #d1d5db',
                borderRadius: 4,
                padding: '1px 4px',
                outline: 'none',
                width: '100%',
                background: '#fff',
                lineHeight: 1.45,
              }}
            />
          ) : (
            proof.text
          )}
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation()
            deleteProofPoint({ id: proof.id, betId, stage })
          }}
          title="Delete"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: 11,
            color: '#d1d5db',
            padding: '0 2px',
            lineHeight: 1,
            flexShrink: 0,
          }}
        >
          ✕
        </button>
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginTop: 6,
          flexWrap: 'wrap',
        }}
      >
        <input
          type="date"
          value={proof.date}
          onChange={(e) => onChange({ ...proof, date: e.target.value })}
          style={{
            fontSize: 11,
            border: '1px solid #e5e7eb',
            borderRadius: 4,
            padding: '2px 6px',
            color: '#6b7280',
            background: '#fff',
          }}
        />
        <ScoreSelector
          value={proof.score}
          onChange={(s) => onChange({ ...proof, score: s })}
        />
        <select
          value={proof.ownerId ?? ''}
          onChange={(e) => {
            const id = e.target.value || null
            const person = persons.find((p) => p.id === id)
            onChange({
              ...proof,
              ownerId: id,
              ownerName: person?.name ?? null,
            })
          }}
          style={{
            fontSize: 11,
            border: '1px solid #e5e7eb',
            borderRadius: 4,
            padding: '2px 6px',
            color: '#6b7280',
            background: '#fff',
          }}
        >
          <option value="">No owner</option>
          {persons.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  )
}
